/**
 * GiftsPage - Unified hub for sending and receiving audio gifts
 *
 * Combines Send and Received gift functionality in a tabbed interface.
 * URL parameters:
 * - ?tab=received - Shows received gifts (default)
 * - ?tab=send - Shows send gift wizard
 * - ?biographyId=xxx - Pre-selects a biography for sending
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Gift, Inbox, Send } from 'lucide-react';
import { useReceivedGifts } from '@/hooks/useReceivedGifts';
import ReceivedGiftsContent from './ReceivedGiftsPage';
import SendAudioGiftPage from './SendAudioGiftPage';

const GiftsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { unreadCount } = useReceivedGifts();

  // Get active tab from URL, default to 'received'
  const activeTab = searchParams.get('tab') || 'received';

  const handleTabChange = (value: string) => {
    // Preserve other params (like biographyId) when switching tabs
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', value);
    setSearchParams(newParams);
  };

  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Gift className="h-10 w-10 text-pink-500" />
          <h1 className="text-4xl font-bold">Audio Gifts</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Send and receive personalized meditation gifts
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-2 mb-8">
          <TabsTrigger value="received" className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Received
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="send" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Send Gift
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-0">
          <ReceivedGiftsContent embedded />
        </TabsContent>

        <TabsContent value="send" className="mt-0">
          <SendAudioGiftPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GiftsPage;
